import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Copy, CheckCircle2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";

// Reusable TOTP enrollment. mode="auth" uses the authenticated /enroll endpoints;
// mode="challenge" uses the pre-login /setup endpoints with a challenge token.
export const MfaSetup = ({ mode = "auth", challengeToken, onComplete }) => {
  const { mfaSetupStart, mfaSetupVerify } = useAuth();
  const [starting, setStarting] = useState(true);
  const [enroll, setEnroll] = useState(null); // { enrollment_id, otpauth_uri, qr_image }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const begin = async () => {
    setStarting(true);
    try {
      const data = mode === "challenge"
        ? await mfaSetupStart(challengeToken)
        : (await api.post("/auth/mfa/enroll")).data;
      setEnroll(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start MFA setup");
    } finally {
      setStarting(false);
    }
  };

  // Guard against StrictMode double-invoke so we don't spawn duplicate enrollment
  // sessions. Backend is also idempotent (reuses the active session), so the QR/secret
  // stays stable no matter what.
  const startedRef = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (startedRef.current) return; startedRef.current = true; begin(); }, []);

  const verify = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    setBusy(true);
    try {
      if (mode === "challenge") {
        const data = await mfaSetupVerify(challengeToken, enroll.enrollment_id, code);
        setResult(data);
        setRecovery(data.recovery_codes);
      } else {
        const { data } = await api.post("/auth/mfa/enroll/verify", { enrollment_id: enroll.enrollment_id, code });
        setResult(data);
        setRecovery(data.recovery_codes);
      }
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Invalid code");
    } finally {
      setBusy(false);
    }
  };

  const copyCodes = async () => {
    try { await navigator.clipboard.writeText(recovery.join("\n")); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  if (recovery) {
    return (
      <div className="space-y-4" data-testid="mfa-recovery-codes">
        <div className="flex items-center gap-2 text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
          <h3 className="font-display text-lg font-bold">Two-factor authentication is on</h3>
        </div>
        <p className="text-sm text-slate-600">
          Save these <b>recovery codes</b> somewhere safe. Each works once and lets you sign in if you lose your
          authenticator. <b>This is the only time they’ll be shown.</b>
        </p>
        <div className="grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-slate-50 p-4 font-mono-arps text-sm">
          {recovery.map((c) => <span key={c} data-testid="mfa-recovery-code">{c}</span>)}
        </div>
        <div className="flex gap-2">
          <Button data-testid="mfa-copy-recovery" variant="outline" onClick={copyCodes}>
            <Copy className="mr-1.5 h-4 w-4" />{copied ? "Copied" : "Copy codes"}
          </Button>
          <Button data-testid="mfa-recovery-done" className="bg-slate-900 text-white hover:bg-slate-800" onClick={() => onComplete?.(result)}>
            I’ve saved them — continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="mfa-setup">
      <div className="flex items-center gap-2 text-slate-900">
        <ShieldCheck className="h-5 w-5 text-emerald-600" />
        <h3 className="font-display text-lg font-bold">Set up two-factor authentication</h3>
      </div>
      <p className="text-sm text-slate-600">
        Scan this QR code with an authenticator app (Google Authenticator, 1Password, Authy…), then enter the
        6-digit code it shows.
      </p>
      {starting || !enroll ? (
        <div className="flex h-52 items-center justify-center text-slate-400" data-testid="mfa-setup-loading">Preparing secure setup…</div>
      ) : (
        <>
          <div className="flex justify-center">
            <img data-testid="mfa-qr" src={enroll.qr_image} alt="Scan with your authenticator app" className="h-52 w-52 rounded-lg border border-slate-200 bg-white p-2" />
          </div>
          <details className="text-xs text-slate-500">
            <summary className="cursor-pointer">Can’t scan? Enter the key manually</summary>
            <code data-testid="mfa-manual-key" className="mt-2 block break-all rounded bg-slate-100 p-2 text-slate-700">{enroll.otpauth_uri}</code>
          </details>
          <form onSubmit={verify} className="space-y-3">
            <div className="flex items-center gap-2">
              <KeyRound className="h-4 w-4 text-slate-400" />
              <Input
                data-testid="mfa-setup-code"
                inputMode="numeric"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="font-mono-arps tracking-widest"
              />
            </div>
            <Button data-testid="mfa-setup-verify" type="submit" disabled={busy || code.length !== 6} className="w-full bg-slate-900 text-white hover:bg-slate-800">
              {busy ? "Verifying…" : "Verify & activate"}
            </Button>
          </form>
        </>
      )}
    </div>
  );
};
